import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, XCircle, AlertTriangle, Scale, Users, Anchor, Building, ArrowRight, Shield, Heart } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const IsAmyRightForYouPage = () => {
  // HowTo schema for evaluating if you have a strong case
  const howToSteps = {
    name: "How to Evaluate If You Have a Strong Personal Injury Case",
    description: "Steps to assess whether your injury case is likely to result in fair compensation and whether Amy C. Brown is the right attorney for you.",
    totalTime: "PT10M",
    steps: [
      { name: "Identify the Type of Case", text: "Determine if your case falls within Amy's practice areas: car crashes, motorcycle accidents, maritime/Jones Act, first responder injuries, slip and falls, or wrongful death." },
      { name: "Assess Evidence of Fault", text: "Consider if there's clear evidence the other party was at fault: police reports, witnesses, photos, video footage, or admissions." },
      { name: "Document Your Injuries", text: "Evaluate the severity of your injuries. Strong cases typically involve documented medical treatment, surgery, therapy, or permanent effects." },
      { name: "Calculate Your Financial Losses", text: "Add up medical bills, lost wages, and future earning impacts. Significant financial damages strengthen your case." },
      { name: "Check Insurance Coverage", text: "Determine if the at-fault party has insurance or assets. Even strong cases need a collectible defendant to result in compensation." },
      { name: "Review Your Post-Accident Actions", text: "Consider whether you sought prompt medical care and avoided making statements without legal counsel." },
      { name: "Schedule a Free Consultation", text: "Contact Amy for an honest assessment. She'll tell you directly if she's the right fit or if another attorney would serve you better." }
    ]
  };

  const casesTaken = [
    'Car crashes',
    'Motorcycle accidents', 
    'Slip and falls',
    'First responder injuries',
    'Jones Act / Maritime claims',
    'Seaman injuries',
    'Wrongful death (from above case types)'
  ];

  const casesNotTaken = [
    'Family law',
    'Wills and estates',
    'Traffic tickets',
    'DUI cases',
    'Criminal defense matters',
    'Cases outside Washington or Alaska (except maritime/Jones Act)'
  ];

  const strongCaseFactors = [
    {
      title: 'Clear evidence of fault',
      description: 'Police report, witnesses, photos, video, or admission from the at-fault party'
    },
    {
      title: 'Serious, documented injuries',
      description: 'Ongoing medical treatment — surgery, therapy, long-term care, or permanent effects'
    },
    {
      title: 'Significant financial losses',
      description: 'High medical bills, lost wages, future earning impact'
    },
    {
      title: 'Collectible defendant',
      description: 'The at-fault party has insurance or assets that can actually pay a fair settlement'
    },
    {
      title: 'Reasonable post-accident behavior',
      description: 'You sought prompt medical care and didn\'t make major statements without counsel'
    }
  ];

  const weakCaseFactors = [
    {
      title: 'No clear evidence of fault',
      description: '"He said/she said" with no witnesses or video — heavily disputed liability'
    },
    {
      title: 'Minor or undocumented injuries',
      description: 'Soft-tissue pain with no follow-up care or imaging'
    },
    {
      title: 'Delayed medical treatment',
      description: 'Weeks or months before seeking care with no explanation'
    },
    {
      title: 'Significant contributory fault',
      description: 'Major speeding, DUI, or ignoring obvious danger on your part'
    },
    {
      title: 'Uncollectible defendant',
      description: 'The at-fault party has little or no insurance and no real assets'
    }
  ];

  // FAQ data for AI search optimization
  const faqData = [
    {
      question: "What types of cases does Amy C. Brown take?",
      answer: "Amy takes personal injury and maritime injury cases in Washington and Alaska including car crashes, motorcycle accidents, slip and falls, first responder injuries, Jones Act/maritime claims, seaman injuries, and wrongful death cases from these categories."
    },
    {
      question: "What cases does Amy C. Brown NOT take?",
      answer: "Amy does not take family law, wills and estates, traffic tickets, DUI cases, criminal defense matters, or cases outside Washington or Alaska (with one exception: maritime/Jones Act cases which she considers regardless of location)."
    },
    {
      question: "Does Amy have a minimum case value threshold?",
      answer: "No. Amy doesn't have a strict dollar minimum. If she believes she can help you get fair compensation and the case is within her practice areas, she's happy to talk — even if the potential recovery is modest."
    },
    {
      question: "When might a big law firm be better than Amy C. Brown?",
      answer: "A larger firm might be better for: complex class-action lawsuits requiring dozens of lawyers, cases outside Washington/Alaska (except maritime), or situations involving serious criminal charges alongside injury claims since Amy does not practice criminal law."
    },
    {
      question: "What makes a strong personal injury case?",
      answer: "Strong cases typically have: clear evidence of fault (police report, witnesses, photos), serious documented injuries with ongoing medical treatment, significant financial losses, an at-fault party with insurance or assets, and reasonable post-accident behavior like seeking prompt medical care."
    },
    {
      question: "What are red flags that weaken a personal injury case?",
      answer: "Red flags include: no clear evidence of fault ('he said/she said'), minor or undocumented injuries, significant delays in seeking medical treatment, major contributory fault on your part (speeding, DUI), and an uncollectible defendant with no insurance or assets."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Is Amy Right For You? | Honest Assessment | Amy C. Brown Law"
        description="Not every attorney is right for every case. Learn what cases I take, when a big firm might be better, and what makes a strong vs. weak personal injury case. Radical honesty from Amy C. Brown."
        faqData={faqData}
        howToData={howToSteps}
      />

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-slate-800 via-slate-900 to-secondary overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Heart className="w-4 h-4 text-primary" />
              <span className="text-white/90 text-sm font-medium">Radical Honesty</span>
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Is Amy <span className="text-primary">Right For You?</span>
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              Not every attorney is right for every case. I believe you deserve complete honesty before we ever speak — so here's the truth about what I do, what I don't do, and when someone else might actually be a better fit.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-semibold transition-all hover:shadow-lg"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-8 py-4 rounded-full font-semibold transition-all border border-white/20"
                data-testid="hero-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Cases I Take / Don't Take */}
      <section className="py-20 bg-white" data-testid="cases-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              Cases I Take — And Cases I Don't
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              I focus on personal injury and maritime injury cases in Washington and Alaska. Here's exactly what falls inside and outside my practice.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Cases I Take */}
            <div className="bg-teal-50 rounded-2xl p-8 border border-teal-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-secondary">Cases I Take</h3>
              </div>
              <ul className="space-y-3">
                {casesTaken.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-slate-600 bg-white rounded-lg p-4">
                <strong>No minimum threshold:</strong> I don't have a strict dollar minimum. If I believe I can help you get fair compensation and the case is within my practice areas, I'm happy to talk — even if the potential recovery is modest.
              </p>
            </div>

            {/* Cases I Don't Take */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-slate-600 rounded-xl flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-secondary">Cases I Don't Take</h3>
              </div>
              <ul className="space-y-3">
                {casesNotTaken.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-slate-600 bg-white rounded-lg p-4">
                <strong>One exception:</strong> Maritime/Jones Act cases — I'll consider these even if the injury happened elsewhere because of my specialized experience in this area.
              </p>
            </div>
          </div>

          {/* Mutual Respect Note */}
          <div className="max-w-3xl mx-auto mt-12">
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-heading text-xl font-semibold text-secondary mb-2">A Note on Mutual Respect</h4>
                  <p className="text-slate-600 leading-relaxed">
                    I surround myself with good people and I want to keep that energy in my practice. If someone is mean, disrespectful, or abusive during our first conversation, I will politely decline the case. I won't work with clients who treat me or my staff poorly — life is too short, and I only take cases where we can have mutual respect and trust.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When a Big Firm Might Be Better */}
      <section className="py-20 bg-slate-50" data-testid="big-firm-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-teal-100 px-4 py-2 rounded-full mb-4">
                <Building className="w-4 h-4 text-teal-700" />
                <span className="text-teal-700 text-sm font-medium">Honest Comparison</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
                When a Big Firm Might Be Better
              </h2>
              <p className="text-lg text-slate-600">
                I'm honest with every caller: I'm a solo attorney and proud of it — you get me personally on every case, no handoffs, no case managers. But there are a few situations where a larger firm might actually be a better fit.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-secondary mb-2">Complex Class-Action Lawsuits</h3>
                    <p className="text-slate-600">Cases that require dozens of lawyers and years of coordinated litigation need the resources of a large firm. I'm one person — I can't match a team of 50 attorneys working around the clock on document review.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Scale className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-secondary mb-2">Cases Outside Washington or Alaska</h3>
                    <p className="text-slate-600">If your incident didn't occur in Washington or Alaska, you likely need a local attorney. <strong>One exception:</strong> Maritime/Jones Act cases — I'll consider these regardless of location because of my specialized knowledge. In fact, lawyers from other states have reached out to me specifically for maritime cases, and I've partnered with them when it made sense for the client.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-secondary mb-2">Criminal Charges Alongside Injury Claims</h3>
                    <p className="text-slate-600">If someone was in a crash and then arrested for a felony, they need a firm that can handle both the criminal defense and the injury claim at the same time. <strong>I do not practice criminal law.</strong> You'd need coordinated representation I can't provide alone.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 bg-teal-600 rounded-2xl p-8 text-white text-center">
              <p className="text-lg leading-relaxed">
                If your case needs something I don't offer, I'll be the first to say so and help point you in the right direction — <strong>no ego here.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Strong vs. Weak Cases */}
      <section className="py-20 bg-white" data-testid="case-strength-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              What Makes a Strong vs. Weak Case
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              I look at every case with the same question: <em>"Can I prove someone else's negligence caused real harm, and can we get fair compensation for it?"</em>
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Strong Case Factors */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-secondary">Signs of a Strong Case</h3>
              </div>
              <div className="space-y-4">
                {strongCaseFactors.map((factor, index) => (
                  <div key={index} className="bg-green-50 rounded-xl p-5 border border-green-100">
                    <h4 className="font-semibold text-secondary mb-1">{factor.title}</h4>
                    <p className="text-slate-600 text-sm">{factor.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Weak Case Factors */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-secondary">Red Flags That Weaken a Case</h3>
              </div>
              <div className="space-y-4">
                {weakCaseFactors.map((factor, index) => (
                  <div key={index} className="bg-red-50 rounded-xl p-5 border border-red-100">
                    <h4 className="font-semibold text-secondary mb-1">{factor.title}</h4>
                    <p className="text-slate-600 text-sm">{factor.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Honest Commitment */}
          <div className="max-w-3xl mx-auto mt-12">
            <div className="bg-slate-800 rounded-2xl p-8 text-white">
              <h4 className="font-heading text-xl font-semibold mb-4">My Commitment to You</h4>
              <p className="text-slate-300 leading-relaxed mb-4">
                I'm always straight with you. If your case has too many red flags and low odds of meaningful recovery, I'll tell you upfront — <strong>I won't take it just to take it.</strong>
              </p>
              <p className="text-slate-300 leading-relaxed">
                But if the facts line up and I believe we can fight for real compensation, I'll take it personally and work hard to get you the best possible outcome. That's how I've always practiced, and that's how I'll continue to serve my clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-dark" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Still Not Sure? Let's Talk.
            </h2>
            <p className="text-xl text-white/90 mb-8">
              A quick conversation costs nothing and commits you to nothing. I'll give you an honest assessment of your situation — and if I'm not the right fit, I'll tell you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-white text-primary hover:bg-slate-100 px-8 py-4 rounded-full font-semibold transition-all hover:shadow-lg"
                data-testid="cta-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-8 py-4 rounded-full font-semibold transition-all border border-white/20"
                data-testid="cta-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
            <p className="text-white/70 text-sm mt-6">
              Free consultation · No obligation · Honest answers
            </p>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/pricing" 
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all"
            >
              View Pricing & Costs <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/faqs" 
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all"
            >
              Read FAQs <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/about" 
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full text-slate-700 hover:text-teal-600 hover:shadow-md transition-all"
            >
              About Amy <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IsAmyRightForYouPage;
